import { Card } from '@/components/common'
import { GaugeChart } from '@/components/charts'
import { Clock } from 'lucide-react'
import clsx from 'clsx'

interface TimeToFailureGaugeProps {
  failureHours: number
  confidence?: number
}

export default function TimeToFailureGauge({
  failureHours,
  confidence = 0.85
}: TimeToFailureGaugeProps) {
  const formatTime = (hours: number) => {
    if (hours < 24) return `${hours}h`
    if (hours < 168) return `${Math.round(hours / 24)}d`
    return `${Math.round(hours / 168)}w`
  }

  const getUrgency = () => {
    if (failureHours < 24) return { level: 'critical', color: 'text-status-critical', message: 'Immediate attention required' }
    if (failureHours < 72) return { level: 'high', color: 'text-status-warning', message: 'Schedule maintenance soon' }
    if (failureHours < 168) return { level: 'medium', color: 'text-status-info', message: 'Monitor closely' }
    return { level: 'low', color: 'text-status-healthy', message: 'Operating normally' }
  }

  const urgency = getUrgency()

  return (
    <Card title="Time to Failure Prediction">
      <div className="flex flex-col items-center">
        {/* Constrained gauge container to prevent overflow */}
        <div className="relative mb-4 w-full max-w-[200px] overflow-hidden">
          <div className="w-full">
            <GaugeChart
              value={Math.min(100, (1 - failureHours / 168) * 100)}
              min={0}
              max={100}
              height={140}
              thresholds={{ low: 30, high: 70 }}
            />
          </div>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center mt-6">
              <span className={clsx('text-2xl font-mono font-bold', urgency.color)}>
                {formatTime(failureHours)}
              </span>
            </div>
          </div>
        </div>

        <div className="w-full space-y-3">
          <div className="flex items-center justify-between p-2 bg-surface-900 rounded">
            <span className="text-sm text-surface-400">Urgency Level</span>
            <span className={clsx('text-sm font-medium uppercase', urgency.color)}>
              {urgency.level}
            </span>
          </div>

          <div className="flex items-center justify-between p-2 bg-surface-900 rounded">
            <span className="text-sm text-surface-400">Confidence</span>
            <span className="text-sm font-mono text-surface-200">
              {(confidence * 100).toFixed(0)}%
            </span>
          </div>

          <div className="flex items-center gap-2 p-2 bg-surface-900 rounded">
            <Clock size={14} className="text-surface-500" />
            <span className="text-sm text-surface-400">{urgency.message}</span>
          </div>
        </div>
      </div>
    </Card>
  )
}
